import {
    SingleScreenerData,
    getFieldValueBySourceField,
} from '@cfra-nextgen-frontend/shared/src/components/Form/shared/utils';
import { CustomValueFormatterTypes } from '@cfra-nextgen-frontend/shared/src/components/Form/types/filters';
import {
    IMetadataFields,
    ScreenerData,
    ScreenerEtfData,
    ScreenerResearchCompanyData,
    ScreenerResearchData,
} from '@cfra-nextgen-frontend/shared/src/components/Screener/types/screener';
import { MetadataFieldDefinition } from '@cfra-nextgen-frontend/shared/src/components/types/fieldMetadata';
import {
    formatNumberTo2DecimalPlaces,
    getMomentObjectFrom,
    getNumberWithCommas,
    getValuesByPath,
    getValuesByPathDotsIndependent,
    removeInaccuracy,
    scaleCharacterToNumber,
    standardDateFormat,
} from '@cfra-nextgen-frontend/shared/src/utils';
import { ValueFormatterFunc } from 'ag-grid-community';
import moment from 'moment';
import { defaultValueTemplate, fillTemplate } from './templates';
import { ViewdataFieldDefinition } from '../../types/fieldViewData';

export function applyScale({
    fieldMetadata,
    value,
}: {
    fieldMetadata: MetadataFieldDefinition;
    value: number;
}): number {
    if (fieldMetadata.scale) {
        if (!['h', 'k', 'm', 'b'].includes(fieldMetadata.scale.toLocaleLowerCase())) {
            throw new Error(
                `The scale for ${fieldMetadata.label} column contains value out of expected range - "${fieldMetadata.scale}".`,
            );
        }
        const isLowercaseScale = /^[a-z]+$/.test(fieldMetadata.scale);

        const coefficient =
            scaleCharacterToNumber[fieldMetadata.scale.toLocaleLowerCase() as keyof typeof scaleCharacterToNumber];

        if (coefficient) {
            return removeInaccuracy(isLowercaseScale ? value * coefficient : value / coefficient);
        }
    }
    return value;
}

function applyNumberFormat(fieldMetadata: MetadataFieldDefinition, value: number): string {
    const throwFormatError = () => {
        throw new Error(
            `The format for ${fieldMetadata.label} column contains value out of expected range - "${fieldMetadata.format}".`,
        );
    };

    if (!fieldMetadata.format) {
        throw new Error(`The format for ${fieldMetadata.label} is empty.`);
    }

    if (!fieldMetadata.format.includes('#') && !fieldMetadata.format.includes('.')) {
        throwFormatError();
    }

    let result;

    if (fieldMetadata.format.includes('#')) {
        result = String(Math.round(value));
    } else {
        let splittedFormat = fieldMetadata.format.split('.');
        if (splittedFormat.length < 2) {
            throwFormatError();
        }

        if (fieldMetadata.rounding_method === 'cut off') {
            result = formatNumberTo2DecimalPlaces(value, true);
        } else {
            result = parseFloat(String(value)).toFixed(splittedFormat[1].length);
        }
    }

    return result;
}

export function formatNumber({
    fieldMetadata,
    value,
    applyCommas = true,
    skipScale,
}: {
    fieldMetadata: MetadataFieldDefinition;
    value: number;
    applyCommas?: boolean;
    skipScale?: boolean;
}): string {
    if (!skipScale) {
        value = applyScale({ fieldMetadata, value });
    }

    let result = applyNumberFormat(fieldMetadata, value);

    if (applyCommas) {
        result = getNumberWithCommas(result);
    }

    return result;
}

function formatDate(fieldMetadata: MetadataFieldDefinition, value: string): string {
    const toLocalTimezone = !!fieldMetadata?.local_timezone
    return getMomentObjectFrom(value, fieldMetadata.input_format || standardDateFormat, toLocalTimezone).format(fieldMetadata.format);
}

export const defaultNoResultsSymbol = '-';
export const defaultNoRatingText = 'Not Rated';

type GetScreenerValueFormatter = {
    allFieldsMetadata?: Array<IMetadataFields>;
    fieldMetadata: MetadataFieldDefinition;
    screenerData?: ScreenerEtfData | ScreenerData | ScreenerResearchData | ScreenerResearchCompanyData;
    noResultsSymbol: string;
    getValuesByPathLocal?: (source: any, valuePath?: string) => Array<any>;
    useRowDataInValueTemplate?: boolean;
    useGetValuesByPathDotsIndependent?: boolean;
    templateCustomValuesConfiguration?: ViewdataFieldDefinition['custom_values'];
};

export function getScreenerValueFormatter({
    fieldMetadata,
    screenerData,
    noResultsSymbol = defaultNoResultsSymbol,
    getValuesByPathLocal,
    useRowDataInValueTemplate,
    templateCustomValuesConfiguration,
    useGetValuesByPathDotsIndependent,
    allFieldsMetadata,
}: GetScreenerValueFormatter): ValueFormatterFunc {
    return function (params): string {
        if (
            fieldMetadata.value_formatter &&
            fieldMetadata.source_fields &&
            fieldMetadata.source_fields.length > 0 &&
            screenerData
        ) {
            switch (fieldMetadata.value_formatter) {
                case CustomValueFormatterTypes.IsUserEnabled:
                    return getBooleanStringValue(
                        isUserEnabledFormatter(fieldMetadata.source_fields!, {
                            rowData: params.data,
                            viewdata: screenerData._viewdata,
                            metadata: screenerData._metadata,
                        }),
                    );
                case CustomValueFormatterTypes.KeyValueString:
                    return (getValuesByPath(params.data, params.colDef.field) as Array<string>)
                        .map((item) => {
                            const keyValue = item.split('|');
                            return keyValue[0] || false;
                        })
                        .filter((item) => Boolean(item))
                        .sort()
                        .join(', ');
                default:
                    return noResultsSymbol;
            }
        }

        if (!params.colDef.field) {
            return noResultsSymbol;
        }

        const _getValuesByPath = getValuesByPathLocal || getValuesByPath;

        const rawValue =
            (_getValuesByPath(params.data, params.colDef.field) as Array<string>)
                .filter((item) => Boolean(item))
                .sort()
                .join(', ') || undefined; // separate values by comma in the same cell

        let templateDataObject: any = undefined;

        if (useRowDataInValueTemplate && allFieldsMetadata) {
            templateDataObject = {
                ...params.data,
                ...(templateCustomValuesConfiguration &&
                    templateCustomValuesConfiguration.reduce((acc, item) => {
                        const { key, metadata_key, field, is_formatted } = item;
                        const fieldMetadata = allFieldsMetadata?.find(
                            (fieldMetadata) => Object.keys(fieldMetadata)[0] === metadata_key,
                        )?.[metadata_key];

                        if (!fieldMetadata) {
                            return acc;
                        }

                        let value: Array<any> | string = getValuesByPathDotsIndependent(params.data, field);

                        if (!value || value.length === 0) {
                            return acc;
                        }

                        if (is_formatted) {
                            value = applyFieldMetadataToValue({
                                fieldMetadata,
                                rawValue: value[0],
                                noResultsSymbol,
                            });
                        }

                        if (value) {
                            acc[key] = value;
                        }
                        return acc;
                    }, {} as Record<string, any>)),
            };
        }

        return applyFieldMetadataToValue({
            fieldMetadata,
            rawValue,
            noResultsSymbol,
            templateDataObject,
            useGetValuesByPathDotsIndependent,
        });
    };
}

export function getBooleanStringValue(value: any) {
    return value ? 'Yes' : 'No';
}

export function applyFieldMetadataToValue({
    fieldMetadata,
    rawValue,
    skipValueTemplate,
    skipScale,
    noResultsSymbol = defaultNoResultsSymbol,
    useGetValuesByPathDotsIndependent,
    templateDataObject,
}: {
    fieldMetadata: MetadataFieldDefinition;
    rawValue: any;
    skipValueTemplate?: boolean;
    skipScale?: boolean;
    noResultsSymbol?: string;
    useGetValuesByPathDotsIndependent?: boolean;
    templateDataObject?: any;
}) {
    let result = noResultsSymbol;

    if (fieldMetadata.type === 'boolean') {
        result = getBooleanStringValue(rawValue);
    } else if (rawValue === null || rawValue === undefined) {
        if (fieldMetadata.no_value_symbol) {
            return fieldMetadata.no_value_symbol;
        }
        return noResultsSymbol;
    }

    if (!['string', 'number', 'date', 'boolean', 'boolean_nullable', 'number_nullable'].includes(fieldMetadata.type)) {
        throw new Error(`Pointed invalid column metadata type - "${fieldMetadata.type}"`);
    }

    if (fieldMetadata.type === 'string') {
        result = String(rawValue);
    }

    if (['number', 'number_nullable'].includes(fieldMetadata.type)) {
        result = formatNumber({
            fieldMetadata: fieldMetadata,
            value: Number(rawValue),
            skipScale: skipScale,
        });
    }

    if (fieldMetadata.type === 'date') {
        result = formatDate(fieldMetadata, rawValue);
        if (result === 'Invalid date') {
            return noResultsSymbol;
        }
    }

    if (skipValueTemplate) {
        return result;
    }

    const valueTemplate =
        !fieldMetadata.value_template && fieldMetadata.symbol === '%'
            ? defaultValueTemplate
            : fieldMetadata.value_template;
    if (valueTemplate) {
        result =
            fillTemplate({
                templateName: 'value_template',
                template: valueTemplate,
                dataObject: templateDataObject || fieldMetadata,
                formattedValue: result,
                useGetValuesByPathDotsIndependent,
            }) || result;
    }

    return result;
}

export const sliderApplyScaleToValue = ({
    value,
    isPercentageDataPoint,
    itemMetadata,
}: {
    value: number;
    isPercentageDataPoint: boolean;
    itemMetadata: MetadataFieldDefinition;
}) => {
    return applyScale({
        fieldMetadata: itemMetadata,
        value: isPercentageDataPoint ? Number(Math.ceil(value * 10000) / 10000) : Math.ceil(value),
    });
};

export function sliderUnApplyScaleFromNumericValue(
    fieldMetadata: MetadataFieldDefinition,
    valueWithAppliedFieldMetadata: string,
) {
    const parsedInputValue = parseFloat(valueWithAppliedFieldMetadata.replaceAll(',', ''));
    if (!fieldMetadata.scale) {
        return parsedInputValue;
    }
    const isLowerCaseScale = fieldMetadata.scale.toLocaleLowerCase() === fieldMetadata.scale;
    return applyScale({
        fieldMetadata: {
            ...fieldMetadata,
            scale: (isLowerCaseScale
                ? fieldMetadata.scale.toUpperCase()
                : fieldMetadata.scale.toLowerCase()) as typeof fieldMetadata.scale,
        },
        value: parsedInputValue,
    });
}

export function isUserEnabledFormatter(sourceFields: Array<string>, singleScreenerData: SingleScreenerData) {
    if (sourceFields.length !== 2) {
        throw new Error(`Missing source fields for ${CustomValueFormatterTypes.IsUserEnabled}`);
    }

    const fieldValues = sourceFields.map((sourceField) => {
        return getFieldValueBySourceField({ sourceField, ...singleScreenerData });
    });

    const [enabledDate, disabledDate] = fieldValues;

    const currentDateTime = moment.utc();
    const enableDate = moment.utc(enabledDate);
    const disableDate = moment.utc(disabledDate);

    const isEnabled =
        enableDate.isValid() &&
        enableDate.isBefore(currentDateTime) &&
        (!disableDate.isValid() || disableDate.isAfter(currentDateTime));

    return isEnabled;
}
